"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import confetti from "canvas-confetti";
import { ThumbsUp, ThumbsDown, MessageSquareHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { submitFeedback } from "@/app/actions/feedback";
import { formatDistanceToNow } from "date-fns";
export interface Feedback {
  id: string;
  sentiment: string;
  message: string;
  author_name: string;
  created_at: string;
}
export function PolarizationSurvey(t0) {
  const $ = _c(19);
  const {
    initialFeedbacks
  } = t0;
  const [sentiment, setSentiment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setSentiment("love");
      confetti({
        particleCount: 100,
        spread: 70,
        origin: {
          y: 0.6
        }
      });
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleLoveIt = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setSentiment("hate");
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleHateIt = t2;
  let t3;
  if ($[2] !== sentiment) {
    t3 = async e => {
      e.preventDefault();
      if (!sentiment) {
        return;
      }
      setIsSubmitting(true);
      const formData = new FormData(e.currentTarget);
      formData.append("sentiment", sentiment);
      await submitFeedback(formData);
      setSubmitted(true);
      setIsSubmitting(false);
    };
    $[2] = sentiment;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleSubmit = t3;
  const t4 = `relative py-24 transition-colors duration-500 overflow-hidden ${sentiment === "hate" ? "bg-red-950/10" : ""}`;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">Tried Supadmin? Choose Your Side.</h2>;
    t6 = <p className="mt-4 text-lg text-gray-600">Be honest. We can take it (probably).</p>;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  if ($[6] !== handleSubmit || $[7] !== isSubmitting || $[8] !== sentiment || $[9] !== submitted) {
    t7 = !submitted ? <motion.div animate={sentiment === "hate" ? {
      x: [-10, 10, -10, 10, 0]
    } : {}} transition={{
      duration: 0.4
    }} className="mt-12"><div className="flex items-center justify-center gap-6"><Button onClick={handleLoveIt} variant={sentiment === "love" ? "default" : "outline"} size="lg" className={`group relative corner-squircle rounded-full overflow-hidden h-16 px-8 text-lg ${sentiment === "love" ? "ring-2 ring-emerald-500 ring-offset-2" : "hover:border-emerald-200"}`}><div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-500 opacity-0 group-hover:opacity-10 transition-opacity" /><span className={`mr-2 text-2xl transition-transform ${sentiment === "love" ? "scale-110" : "group-hover:scale-110"}`}>❤️</span>Love it</Button><Button onClick={handleHateIt} variant={sentiment === "hate" ? "destructive" : "outline"} size="lg" className={`group relative corner-squircle rounded-full overflow-hidden h-16 px-8 text-lg ${sentiment === "hate" ? "ring-2 ring-red-500 ring-offset-2 shadow-[0_0_30px_rgba(239,68,68,0.4)]" : "hover:border-red-200"}`}><div className="absolute inset-0 bg-gradient-to-r from-red-400 to-rose-500 opacity-0 group-hover:opacity-10 transition-opacity" /><span className={`mr-2 text-2xl transition-transform ${sentiment === "hate" ? "scale-110" : "group-hover:scale-110"}`}>🤬</span>Hate it</Button></div><AnimatePresence mode="wait">{sentiment && <motion.div initial={{
          opacity: 0,
          y: 20,
          height: 0
        }} animate={{
          opacity: 1,
          y: 0,
          height: "auto"
        }} exit={{
          opacity: 0,
          y: -20,
          height: 0
        }} className="mt-8 max-w-lg mx-auto"><form onSubmit={handleSubmit} className="bg-white/80 backdrop-blur block p-6 rounded-2xl border shadow-xl"><div className="mb-4">{sentiment === "love" ? <div className="text-left space-y-2"><h3 className="font-semibold flex items-center text-emerald-800"><MessageSquareHeart className="w-5 h-5 mr-2" />Tell us why you love us in one sentence.</h3><p className="text-sm text-gray-500">(We might publish it on the homepage!)</p><Input name="message" required={true} placeholder="Because it saves me 10 hours a week..." className="bg-white" /></div> : <div className="text-left space-y-2"><h3 className="font-semibold text-red-600">Ouch. You broke our hearts. 💔</h3><p className="text-sm text-gray-600">Tell us why so we can fix it (Be brutal).</p><Textarea name="message" required={true} placeholder={"Because the UI is too white...\nBecause you're missing feature X...\nBecause my cat stepped on the keyboard..."} className="bg-white min-h-[100px] border-red-200 focus-visible:ring-red-500" /></div>}</div><Button type="submit" disabled={isSubmitting} className="w-full">{isSubmitting ? "Sending..." : "Send Feedback"}</Button></form></motion.div>}</AnimatePresence></motion.div> : <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="mt-12 max-w-lg mx-auto p-8 rounded-2xl bg-emerald-50 border border-emerald-100"><h3 className="text-xl font-bold text-emerald-800 mb-2">{sentiment === "love" ? "Aww, we love you too! \uD83E\uDEF6" : "Thanks for the feedback!"}</h3><p className="text-emerald-700">{sentiment === "love" ? "Your message has safely arrived. Keep being awesome." : "(We threw this message at the dev team's desk, they are crying right now). We'll let you know when it's fixed."}</p></motion.div>;
    $[6] = handleSubmit;
    $[7] = isSubmitting;
    $[8] = sentiment;
    $[9] = submitted;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== initialFeedbacks) {
    t8 = initialFeedbacks.length > 0 && <div className="mt-32"><h3 className="text-2xl font-bold text-gray-900 mb-10">What Others Are Saying</h3><div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">{initialFeedbacks.map(_temp)}</div></div>;
    $[11] = initialFeedbacks;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = <div className="mx-auto max-w-4xl px-4 text-center">{t5}{t6}{t7}{t8}</div>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t4 || $[17] !== t9) {
    t10 = <section className={t4}>{t9}</section>;
    $[16] = t4;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}
function _temp(fb) {
  return <div key={fb.id} className="break-inside-avoid bg-white p-6 rounded-2xl border shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] text-left"><div className="flex items-center gap-2 mb-3">{fb.sentiment === "love" ? <span className="inline-flex items-center justify-center p-1.5 rounded-full bg-emerald-100/50 text-[16px]">❤️</span> : <span className="inline-flex items-center justify-center p-1.5 rounded-full bg-red-100/50 text-[16px]">🤬</span>}<div className="flex flex-col"><span className="text-sm font-medium text-gray-900">{fb.author_name}</span><span className="text-xs text-gray-500">{formatDistanceToNow(new Date(fb.created_at), {
            addSuffix: true
          })}</span></div></div><p className="text-gray-700 italic">"{fb.message}"</p></div>;
}